import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { stripeConnectAPI } from '../../services/api';
import {
  Wallet,
  ExternalLink,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  ArrowRight,
  DollarSign,
  TrendingUp,
  CreditCard,
  RefreshCw,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

const StripeConnectSetup = () => {
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  // Check URL params for Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      // Clear the URL param and refresh status
      window.history.replaceState({}, '', window.location.pathname + '?tab=settings');
    }
  }, []);

  // Fetch Stripe Connect availability and status
  useEffect(() => {
    const fetchData = async () => {
      try {
        // First check availability (no auth required)
        const availRes = await stripeConnectAPI.checkAvailability().catch(() => null);
        setAvailability(availRes);
        
        // If Connect is not available, don't bother with status
        if (availRes && !availRes.connect_available) {
          setLoading(false);
          return;
        }
        
        // Check if token exists before making authenticated requests
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Please log in to access payout settings');
          setLoading(false);
          return;
        }
        
        const [statusRes, earningsRes] = await Promise.all([
          stripeConnectAPI.getStatus(),
          stripeConnectAPI.getEarnings().catch(() => null)
        ]);
        setStatus(statusRes);
        setEarnings(earningsRes);
      } catch (err) {
        console.error('Failed to fetch Stripe status:', err);
        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError('Failed to load Stripe Connect status');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Start onboarding
  const handleStartOnboarding = async () => {
    setActionLoading('onboard');
    setError('');
    try {
      const result = await stripeConnectAPI.startOnboarding();
      // Redirect to Stripe's onboarding
      window.location.href = result.url;
    } catch (err) {
      console.error('Failed to start onboarding:', err);
      // Handle error - detail might be string or object
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to start Stripe onboarding. Please try again.';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.message) {
        errorMessage = detail.message;
      }
      
      setError(errorMessage);
      setActionLoading(null);
    }
  };

  // Open Stripe dashboard
  const handleOpenDashboard = async () => {
    setActionLoading('dashboard');
    try {
      const result = await stripeConnectAPI.getDashboardLink();
      if (result.type === 'standard') {
        // For Standard accounts, show a message and open Stripe dashboard
        window.open(result.url, '_blank');
      } else {
        // For Express accounts, use the login link
        window.open(result.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get dashboard link:', err);
      // Fallback to main Stripe dashboard
      window.open('https://dashboard.stripe.com', '_blank');
    } finally {
      setActionLoading(null);
    }
  };

  // Refresh status
  const handleRefreshStatus = async () => {
    setActionLoading('refresh');
    setError('');
    try {
      const statusRes = await stripeConnectAPI.getStatus();
      setStatus(statusRes);
    } catch (err) {
      console.error('Failed to refresh status:', err);
      // Handle invalid/deleted account gracefully
      if (err.response?.data?.diagnostic?.action_required === 'restart_onboarding') {
        setStatus({ connected: false, status: 'not_started' });
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Disconnect Stripe account
  const handleDisconnect = async () => {
    setActionLoading('disconnect');
    setError('');
    try {
      await stripeConnectAPI.disconnect();
      // Reset state to allow fresh reconnection
      setStatus({ connected: false, status: 'not_started' });
      setEarnings(null);
      setShowDisconnectDialog(false);
    } catch (err) {
      console.error('Failed to disconnect:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Failed to disconnect. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  const isConnected = status?.connected && status?.status === 'active';
  const isPending = status?.connected && status?.status !== 'active';
  const isUnavailable = availability && !availability.connect_available;

  // Show unavailable message if Connect is not supported in this region
  if (isUnavailable) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-orange-500/20">
              <AlertCircle className="w-6 h-6 text-orange-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">Stripe Connect Unavailable</h3>
              <p className="text-muted-foreground mb-4">
                {availability.message || "Automatic payouts via Stripe Connect are not currently available for this platform region."}
              </p>
              <div className="bg-secondary/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-foreground font-medium mb-2">Alternative Payout Options:</p>
                <p className="text-sm text-muted-foreground">
                  Please contact <a href="mailto:support@fundthehope.com" className="text-emerald-400 hover:underline">support@fundthehope.com</a> to 
                  arrange alternative payout methods such as bank transfer.
                </p>
              </div>
              {availability.platform_country && (
                <p className="text-xs text-muted-foreground">
                  Platform region: {availability.platform_country}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Status Card */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-teal-600/10 border border-emerald-500/30 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isConnected ? 'bg-emerald-500/20' : isPending ? 'bg-yellow-500/20' : 'bg-gray-500/20'
            }`}>
              <Wallet className={`w-6 h-6 ${
                isConnected ? 'text-emerald-400' : isPending ? 'text-yellow-400' : 'text-gray-400'
              }`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Stripe Connect</h3>
              <p className="text-sm text-muted-foreground">Receive donations directly to your bank</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isConnected && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1">
                <CheckCircle2 className="w-3 h-3" /> Connected
              </Badge>
            )}
            {isPending && (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 gap-1">
                <Clock className="w-3 h-3" /> Pending
              </Badge>
            )}
            {!status?.connected && (
              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 gap-1">
                <AlertCircle className="w-3 h-3" /> Not Connected
              </Badge>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <p className="text-muted-foreground text-sm mb-6">
          {status?.message || 'Connect your Stripe account to receive donations automatically. When donors contribute to your campaigns, the donation goes directly to your account while tips go to the platform.'}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {!status?.connected && (
            <Button
              onClick={handleStartOnboarding}
              disabled={actionLoading === 'onboard'}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
              data-testid="stripe-connect-btn"
            >
              {actionLoading === 'onboard' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Connect Stripe Account
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          
          {isPending && (
            <>
              <Button
                onClick={handleStartOnboarding}
                disabled={actionLoading === 'onboard'}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                {actionLoading === 'onboard' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Complete Setup
              </Button>
              <Button
                variant="outline"
                onClick={handleRefreshStatus}
                disabled={actionLoading === 'refresh'}
                className="border-emerald-500/30"
              >
                {actionLoading === 'refresh' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh Status
              </Button>
            </>
          )}
          
          {isConnected && (
            <>
              <Button
                onClick={handleOpenDashboard}
                disabled={actionLoading === 'dashboard'}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
              >
                {actionLoading === 'dashboard' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Open Stripe Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={handleRefreshStatus}
                disabled={actionLoading === 'refresh'}
                className="border-emerald-500/30"
              >
                {actionLoading === 'refresh' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDisconnectDialog(true)}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Account
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Earnings Card - Only show if connected */}
      {isConnected && earnings && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Your Earnings
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-secondary rounded-lg p-4 text-center">
              <DollarSign className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">
                ${earnings.total_earned?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-muted-foreground">Total Earned</p>
            </div>
            <div className="bg-secondary rounded-lg p-4 text-center">
              <Wallet className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">
                ${earnings.stripe_balance?.available?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-muted-foreground">Available Balance</p>
            </div>
            <div className="bg-secondary rounded-lg p-4 text-center">
              <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">
                ${earnings.stripe_balance?.pending?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {earnings.total_donations || 0} donations received. Payouts are processed automatically by Stripe.
          </p>
        </div>
      )}

      {/* How It Works */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">How Automatic Payouts Work</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-emerald-400 font-bold text-sm">1</span>
            </div>
            <div>
              <p className="font-medium text-foreground">Connect Your Account</p>
              <p className="text-sm text-muted-foreground">
                Set up Stripe Express to link your bank account securely.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-emerald-400 font-bold text-sm">2</span>
            </div>
            <div>
              <p className="font-medium text-foreground">Receive Donations</p>
              <p className="text-sm text-muted-foreground">
                When someone donates to your campaign, the money is automatically routed to your Stripe account.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-emerald-400 font-bold text-sm">3</span>
            </div>
            <div>
              <p className="font-medium text-foreground">Get Paid Automatically</p>
              <p className="text-sm text-muted-foreground">
                Stripe transfers funds to your bank account on a rolling basis.
              </p>
              <div className="mt-2 text-xs bg-card/50 rounded-md p-3 border border-border/50">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-[#635bff]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                  </svg>
                  <span className="font-medium text-foreground/80">Stripe-Estimated Arrival Times</span>
                </div>
                <ul className="space-y-1 pl-2 text-muted-foreground">
                  <li>• <strong className="text-foreground/80">Standard:</strong> 2-3 business days</li>
                  <li>• <strong className="text-foreground/80">Weekends/Holidays:</strong> May add 1-2 extra days</li>
                  <li>• <strong className="text-foreground/80">First payout:</strong> May take 7-14 days (account verification)</li>
                </ul>
                <div className="mt-3 pt-2 border-t border-border/50">
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 16v-4m0-4h.01"/>
                    </svg>
                    <strong>View exact payout dates:</strong> Log into your <a href="https://connect.stripe.com" target="_blank" rel="noopener noreferrer" className="text-[#635bff] hover:underline">Stripe Dashboard</a> → Your balance → "Expected to arrive" dates
                  </p>
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground italic">
                  * All arrival dates are calculated and provided by Stripe based on your bank and account settings
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent className="bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Remove Stripe Account?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will disconnect your Stripe account from the platform. You can reconnect with a new or existing Stripe account at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-400">
              <p className="font-medium mb-1">Note:</p>
              <ul className="list-disc list-inside space-y-1 text-yellow-300/80">
                <li>Any pending payouts will still be processed by Stripe</li>
                <li>Your Stripe account itself will NOT be deleted</li>
                <li>You can reconnect anytime with a fresh onboarding</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDisconnectDialog(false)}
              disabled={actionLoading === 'disconnect'}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDisconnect}
              disabled={actionLoading === 'disconnect'}
            >
              {actionLoading === 'disconnect' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StripeConnectSetup;
